/**
 * What's changed, and taking a copy — the two reads behind the history screen (issue #304).
 *
 * **Two endpoints and no writes at all.** `GET /activity` is #299's paged read of AP16;
 * `GET /archive` is #304's whole-partition read, the one ADR 0020 decision 1 named as its
 * re-visit trigger. Reading a family's own record is not a write, so nothing here proposes and
 * nothing here confirms — this is the only screen in the panel with no `POST` behind it.
 *
 * **The response types are derived from the server's routes, not declared here** — ADR 0025
 * decision 3, which answers ADR 0021 decision 6 at the moment that decision named (mobile as a
 * real second caller) and answers it differently than it expected. They were declared here, as
 * a deliberate *subset* of what the server discloses, on the reasoning that a field the screen
 * does not render is a field this file should not claim to understand.
 *
 * That reasoning does not survive a client we cannot redeploy. A subset is a second, unchecked
 * statement of the contract: it is right until the server renames something inside it, at which
 * point this file goes on compiling and renders `undefined`. Narrowing what a screen *uses* is
 * a job for the screen; narrowing what the contract *says* only hides drift.
 */

import type { FamilyPayloads } from "@pilotfish/api/src/app/contract.ts";
import { type AppApi, appFetch, familyPath } from "@pilotfish/app-api";

/** One entry in the record of changes, as `disclosure.ts` shows it to the family. */
/**
 * **The activity page, as the server answers it** — `FamilyPayloads["GET /activity"]`, derived
 * from `reads.ts` rather than described here (ADR 0025 decisions 3 and 4).
 *
 * The envelope says something the records cannot (#304): `ACTION_RETENTION` keeps a tool call
 * 90 days and a membership or role change for ever, so far enough down the list the record
 * thins to almost nothing. The *instant* that boundary falls on is stated by the server, not
 * computed here — a bundle carrying its own copy of the number would go on drawing the line in
 * the wrong place after the constant moved, with nothing failing to build, and a browser
 * computing it from its own clock would draw it wrong for anybody whose machine is off.
 */
export type ActivityPage = FamilyPayloads["GET /activity"];

/** One row of the record of what changed. */
export type ActivityEntry = ActivityPage["records"][number];

export function fetchActivityPage(
  api: AppApi,
  familyId: string,
  after: string | null,
): Promise<ActivityPage> {
  const query = after === null ? "" : `?after=${encodeURIComponent(after)}`;
  return appFetch<ActivityPage>(api, familyPath(familyId, `/activity${query}`));
}

/** One projected record inside a section of the archive. */
/**
 * **The whole partition** — `FamilyPayloads["GET /archive"]`, derived.
 *
 * One projection, two surfaces (#304): `export_everything` splits this into numbered parts for
 * a chat host's context window, and this route serves it whole as a file. The projection is the
 * server's and is deliberately maximal, so there is nothing for a client to shape.
 */
export type FamilyArchive = FamilyPayloads["GET /archive"];

/** One titled group of the archive. */
export type ArchiveSection = FamilyArchive["sections"][number];

/** One record inside a section. */
export type ArchiveEntry = ArchiveSection["entries"][number];

export function fetchArchive(api: AppApi, familyId: string): Promise<FamilyArchive> {
  return appFetch<FamilyArchive>(api, familyPath(familyId, "/archive"));
}
